import React from 'react';

/**
 * Interface for the generic TranslationComponent props.
 * @template InputProps - The shape of the raw input props.
 * @template TargetProps - The shape of the props expected by the TargetComponent.
 */
interface ITranslationProps<InputProps, TargetProps> {
    /** The raw input props from the data source (e.g., CMS, Optimizely). */
    inputProps: InputProps;
    /** The React component to render after props are translated. */
    TargetComponent: React.ComponentType<TargetProps>;
    /** A function that maps inputProps to the props required by TargetComponent. */
    mapProps: (input: InputProps) => TargetProps;
    /** Optional additional props to pass through to the TargetComponent (e.g., className, block settings). */
    [key: string]: any;
}

/**
 * A generic component that translates input props into the format expected
 * by a target component before rendering it.
 *
 * This allows adapting data from various sources (CMS, Optimizely, etc.)
 * to match the prop requirements of standard project components without
 * modifying the components themselves.
 *
 * @template InputProps - The shape of the raw input props.
 * @template TargetProps - The shape of the props expected by the TargetComponent.
 * @param {ITranslationProps<InputProps, TargetProps>} props - The component props.
 * @returns {React.ReactElement | null} The rendered TargetComponent or null.
 */
const TranslationComponent = <InputProps, TargetProps>({
    inputProps,
    TargetComponent,
    mapProps,
    ...rest // Capture any additional props (like className, block settings)
}: ITranslationProps<InputProps, TargetProps>): React.ReactElement | null => {
    // Perform the props translation using the provided mapping function

    const targetProps = mapProps(inputProps);

    // Render the target component with the translated props and any additional props passed through
    return <TargetComponent {...targetProps} {...rest} />;
};

export default TranslationComponent;